// src/components/Phase1_2Dashboard.js
/**
 * Phase 1.2 Dashboard Component
 * Manages the completion of Phase 1.2: Firestore schema updates,
 * menu item parsing, and automatic pairing suggestions
 */

import React, { useState, useEffect, useCallback } from "react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { firestoreSchemaService } from "../services/firestoreSchemaService";
import { foodMenuParsingService } from "../services/foodMenuParsingService";
import { aiPairingService } from "../services/aiPairingService";
import "./Phase1_2Dashboard.css";

const Phase1_2Dashboard = () => {
  const { currentRestaurant } = useRestaurant();

  // Schema Migration State
  const [schemaMigrationStatus, setSchemaMigrationStatus] = useState(null);
  const [migrationInProgress, setMigrationInProgress] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({
    current: 0,
    total: 0,
  });

  // Food Menu State
  const [foodMenuStatus, setFoodMenuStatus] = useState(null);
  const [foodMenuText, setFoodMenuText] = useState("");
  const [parsingInProgress, setParsingInProgress] = useState(false);

  // Pairing State
  const [pairingStatus, setPairingStatus] = useState(null);
  const [pairingGeneration, setPairingGeneration] = useState(false);

  // Cocktail Generation State
  const [cocktailGeneration, setCocktailGeneration] = useState(false);
  const [cocktailRecommendations, setCocktailRecommendations] = useState([]);

  // Phase Status
  const [phaseCompletion, setPhaseCompletion] = useState({
    schemaMigration: false,
    foodMenuParsing: false,
    pairingGeneration: false,
    cocktailGeneration: false,
  });

  // Load initial status
  const loadAllStatuses = useCallback(async () => {
    if (!currentRestaurant?.id) return;

    try {
      // Load schema migration status
      const schemaStatus = await firestoreSchemaService.checkMigrationStatus(
        currentRestaurant.id
      );
      setSchemaMigrationStatus(schemaStatus);

      // Load food menu status (checking if analyzed menu exists)
      const foodStatus = await loadFoodMenuStatus();
      setFoodMenuStatus(foodStatus);

      // Load pairing status
      const pairingStatusData = await loadPairingStatus();
      setPairingStatus(pairingStatusData);

      // Calculate phase completion
      updatePhaseCompletion(schemaStatus, foodStatus, pairingStatusData);
    } catch (error) {
      console.error("Error loading Phase 1.2 status:", error);
    }
  }, [currentRestaurant?.id]);

  useEffect(() => {
    loadAllStatuses();
  }, [loadAllStatuses]);

  const loadFoodMenuStatus = async () => {
    // Implementation to check if food menu has been parsed
    // This would check the menu_analysis collection
    return {
      hasParsedMenu: false,
      totalDishes: 0,
      lastParsed: null,
    };
  };

  const loadPairingStatus = async () => {
    // Implementation to check pairing generation status
    return {
      totalPairings: 0,
      lastGenerated: null,
      averageConfidence: 0,
    };
  };

  const updatePhaseCompletion = (
    schemaStatus,
    foodStatus,
    pairingStatusData
  ) => {
    setPhaseCompletion({
      schemaMigration: !schemaStatus.needsMigration,
      foodMenuParsing: foodStatus.hasParsedMenu,
      pairingGeneration: pairingStatusData.totalPairings > 0,
      cocktailGeneration: false, // Will be updated when cocktails are generated
    });
  };

  // Schema Migration Functions
  const runSchemaMigration = async () => {
    if (!currentRestaurant?.id) return;

    try {
      setMigrationInProgress(true);
      setMigrationProgress({ current: 0, total: 0 });

      await firestoreSchemaService.migrateRestaurantSchema(
        currentRestaurant.id,
        {
          onProgress: (current, total) => {
            setMigrationProgress({ current, total });
          },
        }
      );

      // Initialize enhanced collections
      await firestoreSchemaService.initializeEnhancedCollections(
        currentRestaurant.id
      );

      // Reload status
      await loadAllStatuses();

      alert("✅ Schema migration completed successfully!");
    } catch (error) {
      console.error("Schema migration failed:", error);
      alert(`❌ Schema migration failed: ${error.message}`);
    } finally {
      setMigrationInProgress(false);
      setMigrationProgress({ current: 0, total: 0 });
    }
  };

  // Food Menu Parsing Functions
  const parseFoodMenu = async () => {
    if (!currentRestaurant?.id || !foodMenuText.trim()) {
      alert("Please enter your food menu text");
      return;
    }

    try {
      setParsingInProgress(true);

      const analysisResult =
        await foodMenuParsingService.parseAndAnalyzeFoodMenu(
          currentRestaurant.id,
          foodMenuText,
          {
            menuType: "full",
            generatePairings: true,
            analysisLevel: "detailed",
          }
        );

      setFoodMenuStatus({
        hasParsedMenu: true,
        totalDishes: analysisResult.totalDishes,
        lastParsed: new Date().toISOString(),
      });

      alert(
        `✅ Food menu parsed successfully! ${analysisResult.totalDishes} dishes analyzed.`
      );
      await loadAllStatuses();
    } catch (error) {
      console.error("Food menu parsing failed:", error);
      alert(`❌ Food menu parsing failed: ${error.message}`);
    } finally {
      setParsingInProgress(false);
    }
  };

  // Pairing Generation Functions
  const generateAutomaticPairings = async () => {
    if (!currentRestaurant?.id) return;

    try {
      setPairingGeneration(true);

      // This would get all beverages and generate pairings for each
      await aiPairingService.generateBeveragePairings(
        currentRestaurant.id,
        null, // This would be modified to handle all beverages
        {
          includeMenuItems: true,
          includeGenericPairings: true,
          maxSuggestions: 10,
        }
      );

      alert(`✅ Generated automatic pairings successfully!`);
      await loadAllStatuses();
    } catch (error) {
      console.error("Pairing generation failed:", error);
      alert(`❌ Pairing generation failed: ${error.message}`);
    } finally {
      setPairingGeneration(false);
    }
  };

  // Cocktail Generation Functions
  const generateCocktailRecommendations = async () => {
    if (!currentRestaurant?.id) return;

    try {
      setCocktailGeneration(true);

      const cocktails = await aiPairingService.generateCocktailRecommendations(
        currentRestaurant.id,
        {
          occasion: "general",
          skillLevel: "intermediate",
          maxRecipes: 15,
          includeClassics: true,
          includeOriginal: true,
        }
      );

      setCocktailRecommendations(cocktails);
      setPhaseCompletion((prev) => ({ ...prev, cocktailGeneration: true }));

      alert(`✅ Generated ${cocktails.length} cocktail recommendations!`);
    } catch (error) {
      console.error("Cocktail generation failed:", error);
      alert(`❌ Cocktail generation failed: ${error.message}`);
    } finally {
      setCocktailGeneration(false);
    }
  };

  // Calculate overall completion percentage
  const calculateCompletionPercentage = () => {
    const completedTasks =
      Object.values(phaseCompletion).filter(Boolean).length;
    return (completedTasks / 4) * 100;
  };

  if (!currentRestaurant) {
    return (
      <div className="phase1-2-dashboard">
        <h2>⚠️ No Restaurant Selected</h2>
        <p>Please select a restaurant to manage Phase 1.2 features.</p>
      </div>
    );
  }

  return (
    <div className="phase1-2-dashboard">
      <div className="phase-header">
        <h2>🚀 Phase 1.2: AI Foundation Setup</h2>
        <div className="completion-badge">
          <span className="completion-percentage">
            {calculateCompletionPercentage().toFixed(0)}% Complete
          </span>
        </div>
      </div>

      <div className="phase-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${calculateCompletionPercentage()}%` }}
          />
        </div>
        <p className="progress-label">
          Complete all 4 tasks to finish Phase 1.2 and unlock Phase 2 features
        </p>
      </div>

      {/* Schema Migration Section */}
      <div className="feature-section">
        <div className="section-header">
          <h3>
            {phaseCompletion.schemaMigration ? "✅" : "📋"}
            1. Schema Migration
          </h3>
          <div className="section-status">
            {schemaMigrationStatus?.needsMigration ? (
              <span className="status-badge pending">Migration Needed</span>
            ) : (
              <span className="status-badge complete">Up to Date</span>
            )}
          </div>
        </div>

        <div className="section-content">
          <p>
            Update your Firestore database schema to support AI-enhanced
            beverage data, pairing information, and advanced analytics.
          </p>

          {schemaMigrationStatus && (
            <div className="status-details">
              <p>
                <strong>Current Version:</strong>{" "}
                {schemaMigrationStatus.currentVersion}
              </p>
              <p>
                <strong>Target Version:</strong>{" "}
                {schemaMigrationStatus.targetVersion}
              </p>
              {schemaMigrationStatus.lastMigration && (
                <p>
                  <strong>Last Migration:</strong>{" "}
                  {new Date(
                    schemaMigrationStatus.lastMigration
                  ).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {migrationInProgress && (
            <div className="progress-section">
              <p>🔄 Migration in progress...</p>
              {migrationProgress.total > 0 && (
                <div className="progress-bar small">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${
                        (migrationProgress.current / migrationProgress.total) *
                        100
                      }%`,
                    }}
                  />
                </div>
              )}
              <p>
                {migrationProgress.current} / {migrationProgress.total} items
                processed
              </p>
            </div>
          )}

          <button
            className="action-button primary"
            onClick={runSchemaMigration}
            disabled={
              migrationInProgress || !schemaMigrationStatus?.needsMigration
            }
          >
            {migrationInProgress
              ? "⏳ Migrating..."
              : "🚀 Run Schema Migration"}
          </button>
        </div>
      </div>

      {/* Food Menu Parsing Section */}
      <div className="feature-section">
        <div className="section-header">
          <h3>
            {phaseCompletion.foodMenuParsing ? "✅" : "🍽️"}
            2. Food Menu Analysis
          </h3>
          <div className="section-status">
            {foodMenuStatus?.hasParsedMenu ? (
              <span className="status-badge complete">
                {foodMenuStatus.totalDishes} Dishes Analyzed
              </span>
            ) : (
              <span className="status-badge pending">Not Analyzed</span>
            )}
          </div>
        </div>

        <div className="section-content">
          <p>
            Upload and analyze your restaurant's food menu to enable intelligent
            food-beverage pairing suggestions.
          </p>

          <div className="food-menu-input">
            <label htmlFor="foodMenuText">
              <strong>Paste your food menu here:</strong>
            </label>
            <textarea
              id="foodMenuText"
              value={foodMenuText}
              onChange={(e) => setFoodMenuText(e.target.value)}
              placeholder="Paste your complete food menu here... Include dish names, descriptions, prices, and categories."
              rows={10}
              disabled={parsingInProgress}
            />
          </div>

          <button
            className="action-button primary"
            onClick={parseFoodMenu}
            disabled={parsingInProgress || !foodMenuText.trim()}
          >
            {parsingInProgress
              ? "⏳ Analyzing Menu..."
              : "🔍 Analyze Food Menu"}
          </button>
        </div>
      </div>

      {/* Pairing Generation Section */}
      <div className="feature-section">
        <div className="section-header">
          <h3>
            {phaseCompletion.pairingGeneration ? "✅" : "🍷"}
            3. Automatic Pairings
          </h3>
          <div className="section-status">
            {pairingStatus?.totalPairings > 0 ? (
              <span className="status-badge complete">
                {pairingStatus.totalPairings} Pairings Generated
              </span>
            ) : (
              <span className="status-badge pending">Not Generated</span>
            )}
          </div>
        </div>

        <div className="section-content">
          <p>
            Generate intelligent pairing suggestions between your beverages and
            food items using AI analysis of flavor profiles and complementary
            characteristics.
          </p>

          <button
            className="action-button primary"
            onClick={generateAutomaticPairings}
            disabled={pairingGeneration || !phaseCompletion.schemaMigration}
          >
            {pairingGeneration
              ? "⏳ Generating Pairings..."
              : "🎯 Generate Pairings"}
          </button>

          {!phaseCompletion.schemaMigration && (
            <p className="prerequisite-note">
              ⚠️ Complete schema migration first
            </p>
          )}
        </div>
      </div>

      {/* Cocktail Generation Section */}
      <div className="feature-section">
        <div className="section-header">
          <h3>
            {phaseCompletion.cocktailGeneration ? "✅" : "🍸"}
            4. Cocktail Recommendations
          </h3>
          <div className="section-status">
            {cocktailRecommendations.length > 0 ? (
              <span className="status-badge complete">
                {cocktailRecommendations.length} Cocktails Generated
              </span>
            ) : (
              <span className="status-badge pending">Not Generated</span>
            )}
          </div>
        </div>

        <div className="section-content">
          <p>
            Generate custom cocktail recipes using your available spirits
            inventory, including both classic and original creations.
          </p>

          <button
            className="action-button primary"
            onClick={generateCocktailRecommendations}
            disabled={cocktailGeneration || !phaseCompletion.schemaMigration}
          >
            {cocktailGeneration
              ? "⏳ Creating Cocktails..."
              : "🍹 Generate Cocktail Menu"}
          </button>

          {!phaseCompletion.schemaMigration && (
            <p className="prerequisite-note">
              ⚠️ Complete schema migration first
            </p>
          )}

          {cocktailRecommendations.length > 0 && (
            <div className="cocktail-preview">
              <h4>Generated Cocktails Preview:</h4>
              <div className="cocktail-list">
                {cocktailRecommendations.slice(0, 3).map((cocktail, index) => (
                  <div key={index} className="cocktail-item">
                    <h5>{cocktail.name}</h5>
                    <p className="cocktail-type">
                      {cocktail.type} • {cocktail.difficulty}
                    </p>
                    <p className="cocktail-description">
                      {cocktail.description}
                    </p>
                  </div>
                ))}
                {cocktailRecommendations.length > 3 && (
                  <p className="more-cocktails">
                    ...and {cocktailRecommendations.length - 3} more cocktails
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phase Completion Status */}
      {calculateCompletionPercentage() === 100 && (
        <div className="completion-celebration">
          <h3>🎉 Phase 1.2 Complete!</h3>
          <p>
            Excellent! You've successfully set up the AI foundation for your
            beverage platform. You're now ready to move on to{" "}
            <strong>Phase 2: Core AI Features</strong>.
          </p>
          <div className="next-steps">
            <h4>Phase 2 will include:</h4>
            <ul>
              <li>🏆 Professional ratings aggregation and display</li>
              <li>🌍 Producer/history/region information UI</li>
              <li>🎯 Intelligent pairing system interface</li>
              <li>🔍 Advanced search and recommendation engine</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Phase1_2Dashboard;
